import React, { useEffect, useMemo, useState, useRef } from "react";
import { CButton, CCard, CCardGroup, CCol, CForm, CRow, CSpinner } from "@coreui/react";
import { dashboard } from "@/libs/routes";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { tab_key } from "@/constants/translation/common.translation";
import { useFormik } from "formik";
import CustomInput from "@/components/CustomInput";
import settings from "@/constants/translation/setting.translation";
import { useNavigate } from "react-router-dom";
import { edit_profile } from "@/constants/translation/auth.translation";
import { useGetSettingData, useUpdateSettingData } from "@/hooks";
import SettingValidationClass from "@/helpers/validations/setting";
import TabNavigation from "@/components/TabNavigation";
import constants from "@/libs/constant";

/**
 * @name GeneralSetting
 *
 * @description
 * The GeneralSetting component provides a form for managing general settings such as header logo, email logo, favicon logo, and login page logo.
 * It initializes the form fields with existing data, validates inputs, and updates the settings via an API call.
 *
 * The component:
 * - Loads existing general settings on page load.
 * - Uses Formik for form state management and validation.
 * - Allows uploading of image files for logos.
 * - Submits updated settings to the API.
 * - Displays success messages on successful update.
 *
 * @returns {JSX.Element} The JSX for rendering the general settings form.
 */
const GeneralSetting = () => {
  const navigate = useNavigate();
  const t = useTranslationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionBlockRef = useRef(false);

  const mutation = useUpdateSettingData(
    (response) => {
      toast.success(response.message); // Show success message on successful update
      doGetSettingData(); // Refetch data to get updated settings
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state
      }, 1000); // Short delay to prevent immediate re-submission
    },
    (error) => {
      toast.error(error.response?.data?.message || 'Failed to update settings');
      setTimeout(() => {
        submissionBlockRef.current = false;
        setIsSubmitting(false); // Reset submission state on error
      }, 1000); // Short delay to prevent immediate re-submission
    }
  );

  const { isPending } = mutation;

  // Handler to restrict input to numbers and dots only
  const handleVersionKeyPress = (e) => {
    const char = String.fromCharCode(e.which);
    // Allow: numbers (0-9), dots (.), and control keys (backspace, delete, tab, etc.)
    if (!/[0-9.]/.test(char) && e.which !== 8 && e.which !== 46 && e.which !== 9) {
      e.preventDefault();
    }
  };

  /**
   * !This API will call while Page Load and set data
   */

  const validationSchema = useMemo(
    () => SettingValidationClass.generalSettingValidation(t),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      [constants.formFields.generalSettings.headerLogo]: "",
      [constants.formFields.generalSettings.emailLogo]: "",
      [constants.formFields.generalSettings.faviconLogo]: "",
      [constants.formFields.generalSettings.loginPageLogo]: "",
      [constants.formFields.generalSettings.iosVersion]: "",
      [constants.formFields.generalSettings.androidVersion]: "",
      [constants.formFields.generalSettings.contactEmail]: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (submissionBlockRef.current || isSubmitting || isPending) {
        return; // Prevent multiple submissions
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      mutation.mutate(values);
    },
  });

  const {
    refetch: doGetSettingData,
    isLoading: isLoadingData,
    data: getConfiguration,
  } = useGetSettingData();
  useEffect(() => {
    if (!isLoadingData && getConfiguration && getConfiguration.data) {
      const data = getConfiguration.data;
      
      formik.setValues({
        [constants.formFields.generalSettings.headerLogo]: data.home_page_general_header_logo || "",
        [constants.formFields.generalSettings.emailLogo]: data.home_page_general_email_logo || "",
        [constants.formFields.generalSettings.faviconLogo]: data.home_page_general_favicon_logo || "",
        [constants.formFields.generalSettings.loginPageLogo]: data.home_page_general_login_page_logo || "",
        [constants.formFields.generalSettings.iosVersion]: data.setting_ios_app_version || "",
        [constants.formFields.generalSettings.androidVersion]: data.setting_android_app_version || "",
        [constants.formFields.generalSettings.contactEmail]: data.admin_contact_email || "",
      });
    }
  }, [getConfiguration, isLoadingData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Formik setup to manage form state and validation

  const tabs = [
    {
      label: t(tab_key.settings.general_settings),
      path: '',
    }
  ]
  return (
    <>
      <CCardGroup>
        <CCard className="p-4">
          <TabNavigation tabs={tabs} />
          {!isLoadingData && (
            <>
              <CForm onSubmit={formik.handleSubmit}>
                  <CRow>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.header_logo)}
                        type="file"
                        name={constants.formFields.generalSettings.headerLogo}
                        accept={constants.fileFormats.images}
                        value={formik.values[constants.formFields.generalSettings.headerLogo]}
                        onChange={(file) =>
                          formik.setFieldValue(
                            constants.formFields.generalSettings.headerLogo,
                            file
                          )
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched[constants.formFields.generalSettings.headerLogo] &&
                          formik.errors[constants.formFields.generalSettings.headerLogo]
                        }
                      />
                    </CCol>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.favicon_logo)}
                        type="file"
                        name={constants.formFields.generalSettings.faviconLogo}
                        accept={constants.fileFormats.images}
                        value={formik.values[constants.formFields.generalSettings.faviconLogo]}
                        onBlur={formik.handleBlur}
                        onChange={(file) =>
                          formik.setFieldValue(
                            constants.formFields.generalSettings.faviconLogo,
                            file
                          )
                        }
                        error={
                          formik.touched[constants.formFields.generalSettings.faviconLogo] &&
                          formik.errors[constants.formFields.generalSettings.faviconLogo]
                        }
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.login_page_logo)}
                        type="file"
                        name={constants.formFields.generalSettings.loginPageLogo}
                        accept={constants.fileFormats.images}
                        value={formik.values[constants.formFields.generalSettings.loginPageLogo]}
                        onBlur={formik.handleBlur}
                        onChange={(file) =>
                          formik.setFieldValue(
                            constants.formFields.generalSettings.loginPageLogo,
                            file
                          )
                        }
                        error={
                          formik.touched[constants.formFields.generalSettings.loginPageLogo] &&
                          formik.errors[constants.formFields.generalSettings.loginPageLogo]
                        }
                      />
                    </CCol>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.email_logo)}
                        type="file"
                        name={constants.formFields.generalSettings.emailLogo}
                        accept={constants.fileFormats.images}
                        value={formik.values[constants.formFields.generalSettings.emailLogo]}
                        onBlur={formik.handleBlur}
                        onChange={(file) =>
                          formik.setFieldValue(constants.formFields.generalSettings.emailLogo, file)
                        }
                        error={
                          formik.touched[constants.formFields.generalSettings.emailLogo] &&
                          formik.errors[constants.formFields.generalSettings.emailLogo]
                        }
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.ios_version_label)}
                        type="text"
                        name={constants.formFields.generalSettings.iosVersion}
                        value={formik.values[constants.formFields.generalSettings.iosVersion]}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        onKeyPress={handleVersionKeyPress}
                        maxLength={constants.validation.settings.maxVersionLength}
                        error={
                          formik.touched[constants.formFields.generalSettings.iosVersion] &&
                          formik.errors[constants.formFields.generalSettings.iosVersion]
                        }
                      />
                    </CCol>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.android_version_label)}
                        type="text"
                        name={constants.formFields.generalSettings.androidVersion}
                        value={formik.values[constants.formFields.generalSettings.androidVersion]}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        onKeyPress={handleVersionKeyPress}
                        maxLength={constants.validation.settings.maxVersionLength}
                        error={
                          formik.touched[constants.formFields.generalSettings.androidVersion] &&
                          formik.errors[constants.formFields.generalSettings.androidVersion]
                        }
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol lg={6} md={6} sm={12}>
                      <CustomInput
                        label={t(settings.general_settings.contact_email_label)}
                        type="email"
                        name={constants.formFields.generalSettings.contactEmail}
                        value={formik.values[constants.formFields.generalSettings.contactEmail]}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={
                          formik.touched[constants.formFields.generalSettings.contactEmail] &&
                          formik.errors[constants.formFields.generalSettings.contactEmail]
                        }
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol className="d-flex justify-content-center mt-2 gap-2">
                      <CButton
                        color="primary"
                        className="px-4"
                        type="submit"
                        disabled={isPending || isSubmitting || submissionBlockRef.current}
                      >
                        {(isPending || isSubmitting || submissionBlockRef.current) && <CSpinner size="sm" variant="grow" />}
                        {t(edit_profile.submit_btn_text)}
                      </CButton>
                      <CButton
                        color="secondary"
                        className="px-4"
                        type="button"
                        disabled={isPending || isSubmitting || submissionBlockRef.current}
                        onClick={() => {
                          navigate(dashboard);
                        }}
                      >
                        {t(edit_profile.cancel_button)}
                      </CButton>
                      {/* <CButton
                        color="info"
                        className="px-4"
                        type="button"
                        onClick={() => {
                          formik.handleSubmit();
                        }}
                      >
                        Test Submit
                      </CButton> */}
                    </CCol>
                  </CRow>
                </CForm>
            </>
          )}
        </CCard>
      </CCardGroup>
    </>
  );
};

export default GeneralSetting;
